import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "./AdminLayout";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkles, CheckCircle2, Clock, XCircle, Search, RefreshCw,
  ShieldCheck, ShieldX, Eye, ExternalLink, Building2, Trash2, AlertTriangle,
  Zap, Pause, RotateCcw,
} from "lucide-react";

const STATUS_TONE = {
  pending:   "bg-amber-50 text-amber-800 border-amber-200",
  approved:  "bg-sky-50 text-sky-700 border-sky-200",
  live:      "bg-emerald-50 text-emerald-700 border-emerald-200",
  suspended: "bg-rose-50 text-rose-700 border-rose-200",
  rejected:  "bg-slate-100 text-slate-700 border-slate-200",
};
const STATUS_ICON = {
  pending:   Clock,
  approved:  CheckCircle2,
  live:      Zap,
  suspended: ShieldX,
  rejected:  XCircle,
};

const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export default function AdminXpayMerchants() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState("");        // text search
  const [statusFilter, setStatusFilter] = useState(""); // '' | status

  // Decision modal
  const [active, setActive] = useState(null);          // merchant row
  const [actionMode, setActionMode] = useState(null);  // 'approve' | 'reject' | 'suspend' | 'pending'
  const [decisionNote, setDecisionNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Terms modal (MDR % / Rolling Reserve % / API access toggle)
  const [termsRow, setTermsRow]         = useState(null);
  const [termsForm, setTermsForm]       = useState({});
  const [termsBusy, setTermsBusy]       = useState(false);
  // Per-method MDR overrides (iter52). Each row in this array is a
  // {method, pct} pair; we serialise to the API as
  // `mdr_by_method: {card: 2.5, ideal: 1.0, …}`. Empty array → empty
  // dict → clear all overrides on the server.
  const [methodMdr, setMethodMdr]       = useState([]);
  const PAYMENT_METHODS = ["card", "ideal", "bancontact", "paypal", "klarna",
                            "payid", "sepa", "eps", "apple-pay", "google-pay",
                            "interac", "pix", "blik"];
  const openTerms = (row) => {
    setTermsRow(row);
    setTermsForm({
      mdr_pct:                       row.mdr_pct ?? 0,
      rolling_reserve_pct:           row.rolling_reserve_pct ?? 0,
      rolling_reserve_release_days: row.rolling_reserve_release_days ?? 0,
      api_access_enabled:            !!row.api_access_enabled,
    });
    // Hydrate per-method MDR rows from the merchant record.
    const seed = row.mdr_by_method || {};
    setMethodMdr(Object.entries(seed).map(([method, pct]) => ({ method, pct })));
  };
  const closeTerms = () => { setTermsRow(null); setTermsForm({}); setTermsBusy(false); setMethodMdr([]); };
  const submitTerms = async () => {
    if (!termsRow) return;
    setTermsBusy(true);
    try {
      // Build the per-method dict. Drop rows with no method or non-finite
      // percentage so we don't fight the server-side validator.
      const mdr_by_method = {};
      for (const r of methodMdr) {
        const k = (r.method || "").trim().toLowerCase();
        const v = Number(r.pct);
        if (!k || !Number.isFinite(v)) continue;
        mdr_by_method[k] = v;
      }
      await api.patch(`/admin/xpay/merchants/${termsRow.id}/terms`, {
        mdr_pct:                       Number(termsForm.mdr_pct),
        rolling_reserve_pct:           Number(termsForm.rolling_reserve_pct),
        rolling_reserve_release_days: Number(termsForm.rolling_reserve_release_days),
        api_access_enabled:            !!termsForm.api_access_enabled,
        mdr_by_method,
      });
      toast.success("Commercial terms updated.");
      closeTerms();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      setTermsBusy(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/xpay/merchants" + (statusFilter ? `?status=${statusFilter}` : ""));
      setRows(r.data?.merchants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load merchants");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openAction = (row, mode) => {
    setActive(row); setActionMode(mode); setDecisionNote(row.notes || "");
  };
  const closeAction = () => {
    setActive(null); setActionMode(null); setDecisionNote(""); setBusy(false);
  };

  // ---- Multi-select + delete (declared BEFORE use in filteredIds / toggleSelectAll
  // so we don't get a temporal-dead-zone ReferenceError once rows load).
  const [selected, setSelected] = useState(new Set());        // merchant ids
  const [confirmDelete, setConfirmDelete] = useState(null);   // null | row | 'bulk'
  const [forceDelete, setForceDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const closeConfirm   = () => { setConfirmDelete(null); setForceDelete(false); setDeleteBusy(false); };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleteBusy(true);
    try {
      if (confirmDelete === "bulk") {
        const ids = Array.from(selected);
        const r = await api.post("/admin/xpay/merchants/bulk-delete", { ids, force: forceDelete });
        const { deleted, skipped, requested } = r.data || {};
        toast.success(`Deleted ${deleted}/${requested}${skipped ? ` (${skipped} skipped)` : ""}.`);
        clearSelection();
      } else {
        const qs = forceDelete ? "?force=true" : "";
        await api.delete(`/admin/xpay/merchants/${confirmDelete.id}${qs}`);
        toast.success(`Deleted merchant ${confirmDelete.business_name || confirmDelete.email}.`);
      }
      closeConfirm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setDeleteBusy(false);
    }
  };

  const filtered = rows.filter((r) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (r.business_name || "").toLowerCase().includes(q)
        || (r.email || "").toLowerCase().includes(q)
        || (r.contact_name || "").toLowerCase().includes(q);
  });
  const filteredIds = useMemo(() => filtered.map((r) => r.id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const submitAction = async () => {
    if (!active || !actionMode) return;
    // Backend stores past-tense states; the modal uses verbs for clearer UX.
    const STATUS_MAP = {
      approve:    "approved",
      reject:     "rejected",
      suspend:    "suspended",
      pending:    "pending",
      "go-live":  "live",
      pause:      "approved",
      reinstate:  "approved",
    };
    const nextStatus = STATUS_MAP[actionMode] || actionMode;
    setBusy(true);
    try {
      await api.patch(`/admin/xpay/merchants/${active.id}/status`,
        { status: nextStatus, notes: decisionNote });
      toast.success(`Merchant ${nextStatus}.`);
      closeAction();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
      setBusy(false);
    }
  };

  const counts = {
    pending:   rows.filter((r) => r.status === "pending").length,
    approved:  rows.filter((r) => r.status === "approved").length,
    live:      rows.filter((r) => r.status === "live").length,
    suspended: rows.filter((r) => r.status === "suspended").length,
    rejected:  rows.filter((r) => r.status === "rejected").length,
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-200 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-indigo-700" />
            </div>
            <div>
              <h1 className="font-display font-bold text-2xl">Xchangely Pay merchants</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Review B2B sign-ups and manage approved merchant accounts.
              </p>
            </div>
          </div>
          <Button onClick={load} variant="outline" size="sm" className="rounded-xl gap-1.5"
                  data-testid="xpay-admin-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>

        {/* Status chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="xpay-admin-status-tabs">
          {[
            { v: "",          l: "All",       n: rows.length },
            { v: "pending",   l: "Pending",   n: counts.pending },
            { v: "approved",  l: "Approved",  n: counts.approved },
            { v: "live",      l: "Live",      n: counts.live },
            { v: "suspended", l: "Suspended", n: counts.suspended },
            { v: "rejected",  l: "Rejected",  n: counts.rejected },
          ].map((s) => (
            <button key={s.l}
                    onClick={() => setStatusFilter(s.v)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors
                      ${statusFilter === s.v
                        ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                        : "bg-background border-border text-foreground hover:bg-muted"}`}
                    data-testid={`xpay-admin-tab-${s.v || "all"}`}>
              {s.l} <span className="text-muted-foreground ml-1">({s.n})</span>
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter by business, email…"
                    className="pl-9 h-9 w-64 text-xs rounded-xl"
                    data-testid="xpay-admin-search" />
          </div>
        </div>

        {/* Bulk-action bar (only visible when items selected) */}
        {selected.size > 0 && (
          <div className="mb-3 flex items-center gap-3 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-200"
               data-testid="xpay-bulk-bar">
            <span className="text-sm font-semibold text-amber-900">
              {selected.size} selected
            </span>
            <button onClick={clearSelection}
                    className="text-[11px] font-semibold text-amber-800 hover:underline"
                    data-testid="xpay-bulk-clear">
              Clear
            </button>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={toggleSelectAll}
                      className="rounded-xl h-8 text-xs"
                      data-testid="xpay-bulk-toggle-all">
                {allFilteredSelected ? "Deselect filtered" : "Select all filtered"}
              </Button>
              <Button size="sm" onClick={() => setConfirmDelete("bulk")}
                      className="rounded-xl gap-1.5 h-8 bg-rose-600 hover:bg-rose-700"
                      data-testid="xpay-bulk-delete">
                <Trash2 className="w-3.5 h-3.5" /> Delete selected
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="xch-card overflow-hidden" data-testid="xpay-admin-table">
          {loading && <div className="p-6 text-xs text-muted-foreground">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No merchants{statusFilter ? ` with status "${statusFilter}"` : ""}.
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <>
              {/* Header row with master select + count */}
              <div className="px-5 py-2.5 flex items-center gap-3 bg-muted/30 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                <input type="checkbox"
                        checked={allFilteredSelected}
                        onChange={toggleSelectAll}
                        aria-label="Select all visible"
                        className="cursor-pointer accent-rose-600"
                        data-testid="xpay-admin-select-all" />
                <span>{filtered.length} merchant{filtered.length === 1 ? "" : "s"}</span>
              </div>
            <div className="divide-y divide-border">
              {filtered.map((m) => {
                const Icon = STATUS_ICON[m.status] || Clock;
                const checked = selected.has(m.id);
                return (
                  <div key={m.id}
                        className={`px-5 py-3.5 flex items-center gap-3 flex-wrap ${checked ? "bg-rose-50/50" : ""}`}
                        data-testid={`xpay-row-${m.id}`}>
                    <input type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelect(m.id)}
                            aria-label={`Select ${m.business_name || m.email}`}
                            className="cursor-pointer accent-rose-600 shrink-0"
                            data-testid={`xpay-row-select-${m.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="font-display font-bold text-sm">{m.business_name || "—"}</span>
                        <Badge className={`text-[10px] uppercase tracking-wider gap-1 ${STATUS_TONE[m.status] || ""}`} variant="outline">
                          <Icon className="w-3 h-3" />{m.status}
                        </Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {m.email}{m.contact_name ? ` · ${m.contact_name}` : ""}
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Created {fmt(m.created_at)}
                        {m.approved_at && ` · approved ${fmt(m.approved_at)}`}
                        {typeof m.link_count === "number" && ` · ${m.link_count} link${m.link_count === 1 ? "" : "s"}`}
                      </div>
                      {m.notes && (
                        <div className="text-[10px] text-muted-foreground italic mt-1">Note: {m.notes}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {/* Approve — only when not yet approved/live */}
                      {!["approved", "live"].includes(m.status) && (
                        <Button size="sm" onClick={() => openAction(m, "approve")}
                                className="rounded-xl gap-1.5 bg-sky-600 hover:bg-sky-700"
                                data-testid={`xpay-approve-${m.id}`}>
                          <ShieldCheck className="w-3.5 h-3.5" /> Approve
                        </Button>
                      )}
                      {/* Go-live — flip approved → live (start processing) */}
                      {m.status === "approved" && (
                        <Button size="sm" onClick={() => openAction(m, "go-live")}
                                className="rounded-xl gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                                data-testid={`xpay-go-live-${m.id}`}>
                          <Zap className="w-3.5 h-3.5" /> Go live
                        </Button>
                      )}
                      {/* Pause — soft demote live → approved (still onboarded, just gated) */}
                      {m.status === "live" && (
                        <Button size="sm" variant="outline" onClick={() => openAction(m, "pause")}
                                className="rounded-xl gap-1.5 border-amber-300 text-amber-800 hover:bg-amber-50"
                                data-testid={`xpay-pause-${m.id}`}>
                          <Pause className="w-3.5 h-3.5" /> Pause
                        </Button>
                      )}
                      {/* Terms — available once onboarded (approved or live) */}
                      {["approved", "live"].includes(m.status) && (
                        <Button size="sm" variant="outline" onClick={() => openTerms(m)}
                                className="rounded-xl gap-1.5"
                                data-testid={`xpay-terms-${m.id}`}>
                          <Sparkles className="w-3.5 h-3.5" /> Terms
                        </Button>
                      )}
                      {/* Reinstate — bring suspended/rejected back to approved (not auto-live) */}
                      {["suspended", "rejected"].includes(m.status) && (
                        <Button size="sm" variant="outline" onClick={() => openAction(m, "reinstate")}
                                className="rounded-xl gap-1.5 border-sky-300 text-sky-700 hover:bg-sky-50"
                                data-testid={`xpay-reinstate-${m.id}`}>
                          <RotateCcw className="w-3.5 h-3.5" /> Reinstate
                        </Button>
                      )}
                      {/* Suspend — instant kill switch (revokes JWTs immediately) */}
                      {["approved", "live"].includes(m.status) && (
                        <Button size="sm" variant="outline" onClick={() => openAction(m, "suspend")}
                                className="rounded-xl gap-1.5 border-rose-300 text-rose-700 hover:bg-rose-50"
                                title="Disconnect this merchant immediately (revokes their session + blocks all processing)"
                                data-testid={`xpay-suspend-${m.id}`}>
                          <ShieldX className="w-3.5 h-3.5" /> Suspend
                        </Button>
                      )}
                      {/* Reject — terminal */}
                      {m.status !== "rejected" && (
                        <Button size="sm" variant="ghost" onClick={() => openAction(m, "reject")}
                                className="rounded-xl gap-1.5 text-rose-700"
                                data-testid={`xpay-reject-${m.id}`}>
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(m)}
                              className="rounded-xl gap-1.5 text-rose-700 hover:bg-rose-50"
                              title="Hard-delete this merchant"
                              data-testid={`xpay-delete-${m.id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </div>

      {/* Decision modal */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) closeAction(); }}>
        <DialogContent className="max-w-md" data-testid="xpay-admin-decision-modal">
          <DialogHeader>
            <DialogTitle className="font-display">
              {actionMode === "approve"  ? "Approve merchant"
                : actionMode === "go-live"  ? "Switch merchant to LIVE"
                : actionMode === "pause"    ? "Pause merchant (back to Approved)"
                : actionMode === "reinstate"? "Reinstate merchant"
                : actionMode === "suspend"  ? "Suspend merchant (kill switch)"
                : actionMode === "reject"   ? "Reject merchant"
                : "Update status"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {active?.business_name} — {active?.email}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Internal note (optional, audit log only)</Label>
            <Textarea value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)}
                        rows={3} className="mt-1 text-sm"
                        placeholder="e.g. KYC docs received; cleared for launch."
                        data-testid="xpay-admin-decision-note" />
          </div>
          <DialogFooter>
            <Button onClick={closeAction} variant="ghost" disabled={busy}>Cancel</Button>
            <Button onClick={submitAction} disabled={busy}
                    className={
                      actionMode === "approve" || actionMode === "reinstate"
                        ? "bg-sky-600 hover:bg-sky-700"
                      : actionMode === "go-live"
                        ? "bg-emerald-600 hover:bg-emerald-700"
                      : actionMode === "pause"
                        ? "bg-amber-600 hover:bg-amber-700"
                      : actionMode === "reject" || actionMode === "suspend"
                          ? "bg-rose-600 hover:bg-rose-700" : ""}
                    data-testid="xpay-admin-decision-confirm">
              {busy ? "Saving…" : `Confirm ${actionMode}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commercial-terms modal (MDR %, Rolling Reserve %, API access) */}
      <Dialog open={!!termsRow} onOpenChange={(o) => { if (!o) closeTerms(); }}>
        <DialogContent className="max-w-md" data-testid="xpay-admin-terms-modal">
          <DialogHeader>
            <DialogTitle className="font-display">Commercial terms</DialogTitle>
            <DialogDescription className="text-xs">
              {termsRow?.business_name} — {termsRow?.email}. Applied to every settled link from now on.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">MDR %</Label>
                <Input type="number" step="0.01" min="0"
                        value={termsForm.mdr_pct ?? 0}
                        onChange={(e) => setTermsForm({ ...termsForm, mdr_pct: e.target.value })}
                        className="mt-1 text-sm" data-testid="xpay-terms-mdr" />
                <p className="text-[10px] text-muted-foreground mt-1">Your fee on every transaction.</p>
              </div>
              <div>
                <Label className="text-xs">Rolling reserve %</Label>
                <Input type="number" step="0.01" min="0" max="50"
                        value={termsForm.rolling_reserve_pct ?? 0}
                        onChange={(e) => setTermsForm({ ...termsForm, rolling_reserve_pct: e.target.value })}
                        className="mt-1 text-sm" data-testid="xpay-terms-reserve-pct" />
                <p className="text-[10px] text-muted-foreground mt-1">Held back for chargeback risk.</p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Reserve release window (days)</Label>
              <Input type="number" min="0" max="365"
                      value={termsForm.rolling_reserve_release_days ?? 0}
                      onChange={(e) => setTermsForm({ ...termsForm, rolling_reserve_release_days: e.target.value })}
                      className="mt-1 text-sm" data-testid="xpay-terms-reserve-days" />
              <p className="text-[10px] text-muted-foreground mt-1">e.g. 90 days. After this elapses the reserve is releasable.</p>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer rounded-xl border border-border p-3 hover:bg-muted/40">
              <input type="checkbox" className="mt-0.5"
                      checked={!!termsForm.api_access_enabled}
                      onChange={(e) => setTermsForm({ ...termsForm, api_access_enabled: e.target.checked })}
                      data-testid="xpay-terms-api-access" />
              <div>
                <div className="text-sm font-semibold">Enable Webhooks & API access</div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Off by default — the merchant&apos;s portal hides the webhook URL / signing secret panel.
                  Turn on only for technically-integrated merchants.
                </p>
              </div>
            </label>

            {/* 🩹 Iter52 — Per-method MDR overrides. Lets ops charge a
                lower fee for low-cost rails (iDEAL, SEPA) and a higher
                fee for card / PayPal. Empty rows are ignored on save;
                rows that don't match a method fall back to the flat MDR. */}
            <div className="rounded-xl border border-border p-3 space-y-2"
                  data-testid="xpay-terms-per-method-mdr">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold">Per-method MDR overrides</div>
                  <p className="text-[11px] text-muted-foreground">
                    Optional. Methods listed here override the flat MDR above.
                    Leave empty to charge the same rate everywhere.
                  </p>
                </div>
                <Button type="button" size="sm" variant="outline"
                          onClick={() => setMethodMdr([...methodMdr, { method: "", pct: "" }])}
                          data-testid="xpay-terms-method-mdr-add">
                  + Add method
                </Button>
              </div>
              {methodMdr.length === 0 && (
                <p className="text-[11px] text-muted-foreground italic">No overrides — all methods use the flat MDR.</p>
              )}
              {methodMdr.map((row, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_120px_auto] gap-2 items-center"
                      data-testid={`xpay-terms-method-mdr-row-${idx}`}>
                  <select
                    value={row.method}
                    onChange={(e) => {
                      const next = [...methodMdr];
                      next[idx] = { ...next[idx], method: e.target.value };
                      setMethodMdr(next);
                    }}
                    className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                    data-testid={`xpay-terms-method-mdr-method-${idx}`}>
                    <option value="">Select method…</option>
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                  <Input type="number" step="0.01" min="0" max="25"
                          value={row.pct}
                          onChange={(e) => {
                            const next = [...methodMdr];
                            next[idx] = { ...next[idx], pct: e.target.value };
                            setMethodMdr(next);
                          }}
                          placeholder="MDR %"
                          className="text-sm"
                          data-testid={`xpay-terms-method-mdr-pct-${idx}`} />
                  <Button type="button" size="sm" variant="ghost"
                            onClick={() => setMethodMdr(methodMdr.filter((_, i) => i !== idx))}
                            data-testid={`xpay-terms-method-mdr-del-${idx}`}>
                    ×
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={closeTerms} variant="ghost" disabled={termsBusy}>Cancel</Button>
            <Button onClick={submitTerms} disabled={termsBusy} data-testid="xpay-terms-save">
              {termsBusy ? "Saving…" : "Save terms"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation modal (single + bulk share one dialog) */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) closeConfirm(); }}>
        <DialogContent className="max-w-md" data-testid="xpay-admin-delete-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              {confirmDelete === "bulk"
                ? `Delete ${selected.size} merchant${selected.size === 1 ? "" : "s"}?`
                : "Delete merchant?"}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {confirmDelete && confirmDelete !== "bulk" && (
                <>
                  <span className="font-semibold text-foreground">
                    {confirmDelete.business_name || "—"}
                  </span>{" "}
                  — {confirmDelete.email}
                  <br />
                </>
              )}
              This permanently removes the merchant record along with every
              payment link and webhook log they own. <strong>Irreversible.</strong>
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2.5 cursor-pointer rounded-xl border border-amber-200 bg-amber-50 p-3">
            <input type="checkbox" className="mt-0.5 accent-rose-600"
                    checked={forceDelete}
                    onChange={(e) => setForceDelete(e.target.checked)}
                    data-testid="xpay-delete-force" />
            <div className="text-[12px] leading-snug">
              <div className="font-semibold text-amber-900">Force delete</div>
              <p className="text-amber-800/90">
                Override the safety check for merchants with settled volume.
                Leave off to skip those (recommended).
              </p>
            </div>
          </label>
          <DialogFooter>
            <Button onClick={closeConfirm} variant="ghost" disabled={deleteBusy}>Cancel</Button>
            <Button onClick={performDelete} disabled={deleteBusy}
                    className="bg-rose-600 hover:bg-rose-700 gap-1.5"
                    data-testid="xpay-delete-confirm">
              <Trash2 className="w-3.5 h-3.5" />
              {deleteBusy ? "Deleting…"
                : confirmDelete === "bulk"
                    ? `Delete ${selected.size}`
                    : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
